import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  ButtonComponent,
  IconButtonComponent,
  InputComponent,
  ModalData,
  ModalRef,
  ModalService,
  SelectComponent,
  SkeletonDirective,
} from '@ui-lib/components';
import {
  IRoleOption,
  IRolesSearchParams,
  IUserModalDataInterface,
  IUserStatus,
  UserAction,
  UserStatusEnum,
} from '../../model';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { UserManagementService } from '../../services/user-management.service';
import {
  CreateUserRequest,
  SortDirection,
  UpdateUserRequest,
  UserByIdResponse,
} from '../../../../core/openapi/bff-admin-api';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  map,
  Observable,
  of,
  scan,
  take,
  tap,
} from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirm-dialog/confirmation-dialog.component';
import { IConfirmationDialogData } from '../../../../shared/components/confirm-dialog/confirmation-dialog-data.interface';
import { UserInformationService } from '../../../../core/services/user/user-information.service';
import { AsyncPipe } from '@angular/common';
import {
  RoleClient,
  SearchRolesRequest,
  SearchRolesResponseItem,
  SearchRolesResponseItemPagedList,
} from '../../../../core/openapi/identity';
import { switchMap } from 'rxjs/operators';
import { telegramLoginValidator } from '../../../../shared/validators/telegram-login.validator';
import { LoadingState } from '../../../../shared/types/loading.type';
import { UserPermissionEnum } from '../../../../core/models/user-permissions.enum';

type UserFormControls = {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  telegramLogin: FormControl<string>;
  role: FormControl<IRoleOption<string> | null>;
};

@Component({
  selector: 'mon-user-modal',
  imports: [
    ReactiveFormsModule,
    IconButtonComponent,
    TranslateModule,
    SkeletonDirective,
    ButtonComponent,
    InputComponent,
    SelectComponent,
    AsyncPipe,
  ],
  templateUrl: './user-modal.component.html',
  styleUrl: './user-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UserManagementService],
})
export class UserModalComponent extends ModalData<IUserModalDataInterface> implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly userManagementService = inject(UserManagementService);
  private readonly modalService = inject(ModalService);
  private readonly modalRef = inject(ModalRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userInformationService = inject(UserInformationService);
  private readonly roleIdentityClient: RoleClient = inject(RoleClient);

  protected userStatus?: IUserStatus;
  private loadedUser: UserByIdResponse | null = null;

  private rolesTotalCount = 0;
  private rolesLoadedCount = 0;
  private readonly rolesInterval = 10 as const;

  protected readonly isUserProcessing: Signal<boolean> = computed(
    () => this.userManagementService.loadingState() === LoadingState.LOADING,
  );
  protected readonly isUserInfoLoaded: Signal<boolean> = computed(
    () => this.userManagementService.loadingState() === LoadingState.READY,
  );

  protected readonly isRoleListLoading: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly UserStatusEnum = UserStatusEnum;
  protected readonly canManageUsers = toSignal(
    this.userInformationService.userPermissions$.pipe(
      map((permissions) => permissions.includes(UserPermissionEnum.UserManage)),
    ),
    { initialValue: false },
  );
  protected readonly canBlockUsers = toSignal(
    this.userInformationService.userPermissions$.pipe(
      map((permissions) => permissions.includes(UserPermissionEnum.UserBlock)),
    ),
    { initialValue: false },
  );

  protected rolesUpstream$ = new BehaviorSubject<IRolesSearchParams>({
    skip: 0,
    take: this.rolesInterval,
  });

  public availableRoles$: Observable<IRoleOption<string>[]> = this.rolesUpstream$.pipe(
    switchMap((params) => this.getModalRoleList(params)),
    scan(
      (acc: IRoleOption<string>[], newbies: IRoleOption<string>[]): IRoleOption<string>[] => [
        ...acc,
        ...newbies,
      ],
      [],
    ),
    tap((items): void => {
      this.rolesLoadedCount = items.length;
    }),
  );

  protected readonly userFormGroup: FormGroup<UserFormControls> = this.fb.group({
    firstName: this.fb.control('', {
      validators: [Validators.required, Validators.maxLength(64)],
      nonNullable: true,
    }),
    lastName: this.fb.control('', {
      validators: [Validators.required, Validators.maxLength(64)],
      nonNullable: true,
    }),
    email: this.fb.control('', {
      validators: [Validators.required, Validators.email, Validators.maxLength(128)],
      updateOn: 'blur',
      nonNullable: true,
    }),
    telegramLogin: this.fb.control('', {
      validators: [telegramLoginValidator()],
      updateOn: 'blur',
      nonNullable: true,
    }),
    role: this.fb.control<IRoleOption<string> | null>(null, {
      validators: [Validators.required],
    }),
  });

  private readonly syncRoleControlDisabledState = effect(() => {
    if (this.canManageUsers()) {
      this.userFormGroup.controls.role.enable({ emitEvent: false });
      return;
    }

    this.userFormGroup.controls.role.disable({ emitEvent: false });
  });

  protected readonly isUserSelf$ = this.userInformationService
    .getCurrentUser()
    .pipe(
      map(
        (user): boolean =>
          this.isEditingMode && user?.id === (this.loadedUser?.id ?? this.modalData.userId),
      ),
    );

  public get invalidFormData(): boolean {
    return this.userFormGroup.invalid || this.userFormGroup.get('role')?.value === null;
  }

  public get isReadonly(): boolean {
    return !this.canManageUsers();
  }

  public get fullName(): string | undefined {
    const fullName =
      `${this.userFormGroup.controls.firstName.value} ${this.userFormGroup.controls.lastName.value}`.trim();

    return fullName;
  }

  public get role(): string | undefined {
    return this.userFormGroup.controls.role.value?.label;
  }

  public get isCreatingMode(): boolean {
    return this.modalData.action === UserAction.Create;
  }

  public get isEditingMode(): boolean {
    return (
      this.modalData.action === UserAction.Edit &&
      this.modalData.userId !== undefined &&
      this.modalData.userId.length > 0
    );
  }

  public ngOnInit(): void {
    if (this.isEditingMode && typeof this.modalData.userId === 'string') {
      this.userManagementService
        .loadUserById(this.modalData.userId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((user: UserByIdResponse): void => {
          this.loadedUser = user;
          this.userFormGroup.setValue({
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            email: user.email ?? '',
            telegramLogin: user.telegramLogin ?? '',
            role: {
              id: user.roleId,
              label: this.modalData.roleName ?? '',
            },
          });

          this.userStatus = {
            ...user.status,
          };
        });
    }
  }

  public onSubmit(): void {
    if (!this.canManageUsers()) return;

    this.userFormGroup.markAllAsTouched();

    if (this.invalidFormData) return;

    const { role, ...requestBase } = this.userFormGroup.getRawValue();
    const roleId = role?.id ?? '';

    let submit$: Observable<void>;

    switch (this.modalData.action) {
      case UserAction.Create: {
        const request: CreateUserRequest = { ...requestBase, roleId };
        submit$ = this.userManagementService.createUser(request);
        break;
      }
      case UserAction.Edit: {
        const request: UpdateUserRequest = {
          ...requestBase,
          id: this.loadedUser?.id ?? this.modalData.userId ?? '',
          roleId,
        };
        submit$ = this.userManagementService.updateUser(request);
        break;
      }
      default: {
        throw new Error('Unsupported action');
      }
    }

    submit$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.modalRef.close(true),
      error: () => this.modalRef.close(true),
    });
  }

  public deleteUser(): void {
    if (
      !this.canManageUsers() ||
      this.userManagementService.loadingState() === LoadingState.LOADING ||
      this.invalidFormData
    ) {
      return;
    }

    this.modalService
      .open<IConfirmationDialogData>({
        component: ConfirmationDialogComponent,
        withoutBackdropClass: true,
        customContainerClass: 'confirmation-dialog',
        data: {
          chip: this.role,
          entityName: this.fullName,
          headerTranslationKey:
            'monitoring.users.modal.action-confirmation-modal.deletion.deletion-warning-title',
          descriptionTranslationKey:
            'monitoring.users.modal.action-confirmation-modal.deletion.deletion-warning-text',
          approvalTranslationKey:
            'monitoring.users.modal.action-confirmation-modal.deletion.delete-user',
          cancellationTranslationKey:
            'monitoring.users.modal.action-confirmation-modal.deletion.abort-deletion',
        },
      })
      .afterClose$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe((result) => {
        this.userManagementService
          .deleteUser(this.loadedUser?.id ?? this.modalData.userId ?? '', this.fullName ?? '')
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.modalRef.close(result);
          });
      });
  }

  public resetPassword(): void {
    if (
      !this.canManageUsers() ||
      this.userManagementService.loadingState() === LoadingState.LOADING ||
      this.invalidFormData
    ) {
      return;
    }

    const id = this.loadedUser?.id ?? this.modalData.userId ?? '';
    const name = this.fullName ?? '';

    this.modalService
      .open<IConfirmationDialogData>({
        component: ConfirmationDialogComponent,
        withoutBackdropClass: true,
        customContainerClass: 'confirmation-dialog',
        data: {
          chip: this.role,
          entityName: this.fullName,
          headerTranslationKey:
            'monitoring.users.modal.action-confirmation-modal.password-reset.password-reset-warning-title',
          descriptionTranslationKey:
            'monitoring.users.modal.action-confirmation-modal.password-reset.password-reset-warning-text',
          approvalTranslationKey:
            'monitoring.users.modal.action-confirmation-modal.password-reset.reset-password',
          cancellationTranslationKey:
            'monitoring.users.modal.action-confirmation-modal.password-reset.abort-password-reset',
        },
      })
      .afterClose$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe((result) => {
        this.userManagementService
          .resetPassword(id, name)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.modalRef.close(result);
          });
      });
  }

  public toggleUserBlockingState(): void {
    if (
      !this.canBlockUsers() ||
      this.userManagementService.loadingState() === LoadingState.LOADING ||
      this.invalidFormData
    ) {
      return;
    }

    const shouldBlock = this.userStatus?.id !== UserStatusEnum.Blocked;
    const id = this.loadedUser?.id ?? this.modalData.userId ?? '';
    const name = this.fullName ?? '';

    this.modalService
      .open<IConfirmationDialogData>({
        component: ConfirmationDialogComponent,
        withoutBackdropClass: true,
        customContainerClass: 'confirmation-dialog',
        data: {
          chip: this.role,
          entityName: this.fullName,
          headerTranslationKey: shouldBlock
            ? 'monitoring.users.modal.action-confirmation-modal.blocking.blocking-warning-title'
            : 'monitoring.users.modal.action-confirmation-modal.blocking.unblocking-warning-title',
          descriptionTranslationKey: shouldBlock
            ? 'monitoring.users.modal.action-confirmation-modal.blocking.blocking-warning-text'
            : 'monitoring.users.modal.action-confirmation-modal.blocking.unblocking-warning-text',
          approvalTranslationKey: shouldBlock
            ? 'monitoring.users.modal.action-confirmation-modal.blocking.block-user'
            : 'monitoring.users.modal.action-confirmation-modal.blocking.unblock-user',
          cancellationTranslationKey: shouldBlock
            ? 'monitoring.users.modal.action-confirmation-modal.blocking.abort-blocking'
            : 'monitoring.users.modal.action-confirmation-modal.blocking.abort-unblocking',
        },
      })
      .afterClose$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe((result) => {
        const action$ = shouldBlock
          ? this.userManagementService.blockUser(id, name)
          : this.userManagementService.unblockUser(id, name);

        action$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.modalRef.close(result);
        });
      });
  }

  public getModalRoleList(params: IRolesSearchParams): Observable<IRoleOption<string>[]> {
    this.isRoleListLoading.set(true);

    const request: SearchRolesRequest = {
      skip: params.skip,
      take: params.take,
      includeTotalCount: true,
      sortExpressions: [
        {
          propertyName: 'name',
          direction: SortDirection.NUMBER_1,
        },
      ],
    };

    return this.roleIdentityClient.roleSearchPost(request).pipe(
      tap((response) => {
        this.rolesTotalCount = response.totalCount ?? 0;
      }),
      map(
        ({ items }: SearchRolesResponseItemPagedList): IRoleOption<string>[] =>
          items?.map(
            (role: SearchRolesResponseItem): IRoleOption<string> => ({
              id: role.id,
              label: role.name,
            }),
          ) ?? [],
      ),
      catchError((): Observable<never[]> => of([])),
      finalize(() => {
        this.isRoleListLoading.set(false);
      }),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public loadMoreRoles(): void {
    if (this.rolesTotalCount > this.rolesLoadedCount) {
      const params = this.rolesUpstream$.getValue();

      this.rolesUpstream$.next({
        skip: params.skip + this.rolesInterval,
        take: params.take,
      });
    }
  }
}
